import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

type ForgotPasswordStep = 'email' | 'question' | 'success';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState<ForgotPasswordStep>('email');
  const [forgotEmail, setForgotEmail] = useState('');
  const [passwordQuestion, setPasswordQuestion] = useState('');
  const [passwordAnswer, setPasswordAnswer] = useState('');
  const [isLoadingForgotPassword, setIsLoadingForgotPassword] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setErrorMessage('');

    if (!email.trim()) {
      setErrorMessage('Please enter your email');
      return;
    }

    if (!password.trim()) {
      setErrorMessage('Please enter your password');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      await login(email.trim(), password);
      setEmail('');
      setPassword('');
      navigate('/');
    } catch (error: any) {
      const errorMsg = error?.message || 'Invalid email or password. Please try again.';
      setErrorMessage(errorMsg);
      setIsLoading(false);
    }
  };

  const handleForgotPasswordEmail = async () => {
    if (!forgotEmail.trim()) {
      alert('Please enter your email');
      return;
    }

    setIsLoadingForgotPassword(true);
    try {
      const response = await authApi.forgotPassword.request(forgotEmail.trim());
      
      if (response && response.hasQuestion) {
        setPasswordQuestion(response.passwordQuestion);
        setForgotPasswordStep('question');
      } else {
        alert('Password recovery question is not set. Please contact administrator.');
      }
    } catch (error: any) {
      let errorMessage = 'Failed to retrieve password question. Please try again.';
      
      if (error?.code === 'ERR_NETWORK' || error?.code === 'ECONNREFUSED' || error?.message?.includes('Network Error')) {
        errorMessage = 'Network error. Please check:\n\n1. API server is running\n2. Check your internet connection';
      } else if (error?.response) {
        errorMessage = error.response.data?.message || `Server error (${error.response.status}). Please try again.`;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    } finally {
      setIsLoadingForgotPassword(false);
    }
  };

  const handleForgotPasswordReset = async () => {
    if (!passwordAnswer.trim()) {
      alert('Please enter your password answer');
      return;
    }

    setIsLoadingForgotPassword(true);
    try {
      await authApi.forgotPassword.reset(forgotEmail.trim(), passwordAnswer.trim());
      setForgotPasswordStep('success');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to reset password. Please check your answer and try again.';
      alert('Error: ' + errorMessage);
    } finally {
      setIsLoadingForgotPassword(false);
    }
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotPasswordStep('email');
    setForgotEmail('');
    setPasswordQuestion('');
    setPasswordAnswer('');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <img src="/logo.png" alt="Staffly" className="h-16 w-auto" />
          </div>
          <p className="mt-2 text-sm text-gray-600">Sign in to your account</p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleLogin}>
          {errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-800">{errorMessage}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errorMessage) setErrorMessage('');
                }}
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errorMessage) setErrorMessage('');
                }}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-sm text-blue-600 hover:text-blue-500"
              disabled={isLoading}
            >
              Forgot Password?
            </button>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Forgot Password</h3>
              <button
                onClick={closeForgotPassword}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            <div className="mt-4">
              {forgotPasswordStep === 'email' && (
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    Enter your email address to retrieve your password recovery question.
                  </p>
                  <div className="mb-4">
                    <label htmlFor="forgot-email" className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      id="forgot-email"
                      type="email"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Enter your email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      disabled={isLoadingForgotPassword}
                    />
                  </div>
                  <button
                    onClick={handleForgotPasswordEmail}
                    disabled={isLoadingForgotPassword}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {isLoadingForgotPassword ? 'Loading...' : 'Continue'}
                  </button>
                </div>
              )}

              {forgotPasswordStep === 'question' && (
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    Please answer your security question to reset your password.
                  </p>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Security Question
                    </label>
                    <div className="p-3 bg-gray-50 border border-gray-300 rounded-md">
                      <p className="text-sm text-gray-900">{passwordQuestion}</p>
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="password-answer" className="block text-sm font-medium text-gray-700">
                      Answer
                    </label>
                    <input
                      id="password-answer"
                      type="text"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Enter your answer"
                      value={passwordAnswer}
                      onChange={(e) => setPasswordAnswer(e.target.value)}
                      disabled={isLoadingForgotPassword}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setForgotPasswordStep('email');
                        setPasswordAnswer('');
                      }}
                      disabled={isLoadingForgotPassword}
                      className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleForgotPasswordReset}
                      disabled={isLoadingForgotPassword}
                      className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      {isLoadingForgotPassword ? 'Loading...' : 'Reset Password'}
                    </button>
                  </div>
                </div>
              )}

              {forgotPasswordStep === 'success' && (
                <div className="text-center">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                    <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Password Reset Successful!</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    A temporary password has been sent to your email address ({forgotEmail}).
                    {'\n\n'}Please check your inbox and use the temporary password to sign in.
                  </p>
                  <button
                    onClick={closeForgotPassword}
                    className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;

